import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCourse, loc } from "@/lib/ethicsApi";
import { fetchTrackRecords } from "@/lib/academyProgress";
import { EthicsImage, RubyShield, STATUS_LABELS } from "./EthicsImage";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { useT } from "@/i18n/LanguageContext";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { ResumeButton, StatusBadge, PhaseProgress } from "@/components/academy/LessonNav";
import { tpl } from "@/lib/academyTheme";

export function phaseStatus(phase, records) {
  const slugs = phase.lessons.map((l) => l.slug);
  const recs = records.filter((r) => slugs.includes(r.lessonSlug));
  if (!recs.length) return "none";
  if (recs.some((r) => r.learningStatus === "Requires Further Practice")) return "renew";
  const strong = recs.filter((r) => ["Practised", "Integrated", "Independent", "Demonstrated"].includes(r.learningStatus));
  if (recs.length === slugs.length && strong.length === slugs.length) return "done";
  if (recs.length === slugs.length) return "ready";
  return "active";
}

export default function EthicsCourse() {
  const { lang, t } = useT();
  const [course, setCourse] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    fetchCourse().then(setCourse).catch(() => {});
    fetchTrackRecords("ethics").then(setRecords);
  }, []);

  const lessonList = course
    ? course.phases.flatMap((p) => p.lessons.map((l) => ({ p, l })))
    : [];

  useSEO({
    title: "Stoic Ethics Course | The Hall of Right Action | StoicOs.ai",
    description: "Explore Stoic ethics as a practical discipline of reality, causality, fate, change, agency and living according to nature.",
    canonical: langCanonical("/academy/ethics", lang),
    alternates: langAlternates("/academy/ethics"),
    og: { title: "Stoic Ethics: The Hall of Right Action", description: "Character, the four virtues, right choice, noblesse, and moral repair.", type: "article" },
    jsonLd: course ? [
      { "@context": "https://schema.org", "@type": "Course",
        name: "Stoic Ethics: The Hall of Right Action",
        description: "A practical Stoic discipline of reality, causality, fate, change, agency and living according to nature.",
        provider: { "@type": "Organization", name: "StoicOs.ai", url: "https://stoicos.ai" },
        url: "https://stoicos.ai/academy/ethics" },
      { "@context": "https://schema.org", "@type": "ItemList",
        itemListElement: lessonList.map(({ p, l }, i) => ({
          "@type": "ListItem", position: i + 1, name: loc(l.title),
          url: `https://stoicos.ai/academy/ethics/phase/${p.slug}/lesson/${l.slug}` })) },
    ] : [],
  });

  if (!course) return <div className="min-h-screen bg-ink" />;
  const media = course.media || {};
  const returning = records.length > 0;
  const last = returning ? [...records].sort((a, b) => (b.lastActivityAt || "").localeCompare(a.lastActivityAt || ""))[0] : null;
  const allLessons = course.phases.flatMap((p) => p.lessons.map((l) => ({ ...l, phaseSlug: p.slug })));
  const lastLesson = last ? allLessons.find((l) => l.slug === last.lessonSlug) : null;
  const firstLesson = allLessons[0];
  const continueTo = lastLesson ? `/academy/ethics/phase/${lastLesson.phaseSlug}/lesson/${lastLesson.slug}` : `/academy/ethics/phase/${firstLesson.phaseSlug}/lesson/${firstLesson.slug}`;

  return (
    <main className="min-h-screen bg-ink" data-testid="ethics-course-page">
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <EthicsImage media={media} assetKey="ethics-00-hall-of-right-action" ratio="" className="absolute inset-0" alt="The Hall of Right Action" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 pb-16 pt-32 w-full">
          <DisciplineSwitcher active="ethics" />
          <div className="flex items-center gap-4 mb-6"><RubyShield size={44} /><span className="text-[10px] tracking-[0.35em] uppercase text-[#c86058]">Academy of Practical Wisdom · Discipline III</span></div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone tracking-tight uppercase">{loc(course.title, lang)} — {loc(course.hallName, lang)}</h1>
          <p className="mt-6 max-w-2xl text-fog font-reading leading-[1.9]">{loc(course.introduction, lang)}</p>
          <p className="mt-3 max-w-2xl text-fog/80 font-reading">{loc(course.heroIntro, lang)}</p>
          <div className="mt-10 flex flex-wrap gap-4" data-testid="ethics-enter-hall">
            <ResumeButton discipline="ethics" course={course} records={records} />
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 py-14 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto border-l-2 border-[#c86058]/60 pl-6 py-1" data-testid="ethics-historical-note">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#c86058] mb-2">Historical Note</p>
          <p className="text-sm text-fog/85 font-reading leading-[1.85]">{loc(course.historicalNote, lang)}</p>
        </div>
      </section>

      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <p className="font-serif text-2xl md:text-3xl text-bone">{(course.fullMovement || []).map((m) => loc(m, lang)).join("  →  ")}</p>
          <p className="text-xs tracking-[0.22em] uppercase text-[#c86058]">{loc(course.thresholdLine, lang)}</p>
        </div>
      </section>

      <section className="border-t border-white/5 py-20 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto border border-[#c86058]/45 shadow-[0_0_28px_rgba(200,96,88,0.22)] rounded-sm p-6 md:p-10" data-testid="ethics-course-frame">
          <h2 className="font-serif text-2xl md:text-3xl text-bone mb-12 text-center">{lang === "nl" ? "De Zes Fasen" : "The Six Phases"}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {course.phases.map((p) => {
              const st = phaseStatus(p, records);
              return (
                <div key={p.slug} data-testid={`ethics-phase-card-${p.slug}-wrap`}
                  className="group border border-[#c8a95a]/20 bg-[#14211a] hover:border-gold/50 transition-colors p-8 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-xl text-bone">{["I","II","III","IV","V","VI"][p.order - 1]} · {loc(p.title, lang)}</h3>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${st === "done" ? "border-gold/60 text-gold" : st === "none" ? "border-emerald-100/25 text-emerald-100/40" : "border-[#c86058]/50 text-[#e8b8b2]"}`}>{STATUS_LABELS[st]}</span>
                    <PhaseProgress phase={p} records={records} accent="#c86058" />
                  </div>
                  <Link to={`/academy/ethics/phase/${p.slug}`} data-testid={`ethics-phase-card-${p.slug}`}
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-[11px] font-semibold tracking-[0.2em] uppercase bg-[#6b2a2a] text-[#f5e4e4] hover:brightness-125 active:brightness-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c86058]">
                    {t("academy.nav.openPhase")} <span aria-hidden>→</span>
                  </Link>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-gold/60">{loc(p.movement, lang)}</p>
                  <p className="text-sm text-fog/80 font-reading leading-relaxed">{loc(p.centralQuestion, lang)}</p>
                  <p className="text-xs text-[#c86058] mt-auto pt-2 border-t border-white/5">{loc(p.coreAbility, lang)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto border border-[#c8a95a]/20 bg-[#14211a] p-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#c86058] mb-4">The Instruments of the Discipline</p>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-xs tracking-[0.18em] uppercase">
            <Link to="/academy/ethics/record" className="text-gold hover:underline" data-testid="ethics-record-link">Record of Right Action</Link>
            <Link to="/academy/ethics/practice" className="text-[#e8b8b2] hover:underline" data-testid="ethics-practice-link">Instruments of Practice</Link>
            <Link to="/academy/ethics/path" className="text-[#e8b8b2] hover:underline" data-testid="ethics-path-link">Path of Right Action</Link>
            <Link to="/academy/ethics/terms" className="text-[#e8b8b2] hover:underline" data-testid="ethics-terms-link">The Terms</Link>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto flex items-center gap-6">
          <div className="w-28 shrink-0 opacity-60"><EthicsImage media={media} assetKey="ethics-14-chamber-of-nature-and-fate" ratio="aspect-[3/2]" alt="" /></div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#c86058]">Final Assessment</p>
            <Link to="/academy/ethics/chamber" className="font-serif text-lg text-bone mt-1 hover:text-gold block" data-testid="ethics-chamber-link">The Chamber of Right Action</Link>
            <p className="text-sm text-fog/70 mt-1">Opens when the six phases have been walked.</p>
          </div>
        </div>
      </section>
    </main>
  );
}
